import { base64UrlToByteArray } from "@entrilia/shared/utilities/stringHelper";
import type { ViewOnlyProtectionKey } from "../../store/state/user/types";

// Frozen contract — must match the backend AesGcmUrlCipher: v{version}.base64url(nonce(12) || ciphertext || tag(16)).
const NONCE_SIZE_BYTES = 12;
const TAG_SIZE_BYTES = 16;
const AES_GCM = "AES-GCM";

interface ParsedEnvelope {
  version: number;
  nonce: Uint8Array<ArrayBuffer>;
  ciphertextWithTag: Uint8Array<ArrayBuffer>;
}

export const isWebCryptoAvailable = (): boolean => !!globalThis.crypto?.subtle;

// Must stay a subset of ReservedSasParameterNames.cs — a name only here could be the protection parameter (fail-open).
export const reservedSasParameterNames = new Set([
  "sv",
  "ss",
  "srt",
  "sp",
  "se",
  "st",
  "spr",
  "sig",
  "si",
  "sip",
  "sr",
  "sdd",
  "ses",
  "scid",
  "saoid",
  "suoid",
  "skoid",
  "sktid",
  "skt",
  "ske",
  "sks",
  "skv",
  "rscc",
  "rscd",
  "rsce",
  "rscl",
  "rsct",
  "comp",
  "restype",
  "snapshot",
  "versionid",
  "timeout",
]);

export const hasNonSasParameter = (url: string): boolean => {
  let searchParams: URLSearchParams;
  try {
    searchParams = new URL(url).searchParams;
  } catch {
    // An unreadable URL is resolved rather than trusted.
    return true;
  }

  return [...searchParams.keys()].some((name) => !reservedSasParameterNames.has(name.toLowerCase()));
};

export const extractEncryptedParameter = (url: string, parameterName: string): string | undefined => {
  try {
    return new URL(url).searchParams.get(parameterName) ?? undefined;
  } catch {
    return undefined;
  }
};

const parseEnvelope = (envelope: string): ParsedEnvelope | undefined => {
  if (!envelope.startsWith("v")) {
    return undefined;
  }

  const separatorIndex = envelope.indexOf(".");
  if (separatorIndex <= 1) {
    return undefined;
  }

  // Digits only, like the backend's `int.TryParse` — `Number()` would also accept `4e2` and `0x2a`.
  const versionText = envelope.slice(1, separatorIndex);
  if (!/^\d+$/.test(versionText)) {
    return undefined;
  }
  const version = Number(versionText);

  let payload: Uint8Array<ArrayBuffer>;
  try {
    payload = base64UrlToByteArray(envelope.slice(separatorIndex + 1));
  } catch {
    return undefined;
  }

  if (payload.length < NONCE_SIZE_BYTES + TAG_SIZE_BYTES) {
    return undefined;
  }

  return {
    version,
    nonce: payload.subarray(0, NONCE_SIZE_BYTES),
    ciphertextWithTag: payload.subarray(NONCE_SIZE_BYTES),
  };
};

// Ordered, not filtered: a stale version tag must still fall through to the other held keys.
const orderKeysByPreferredVersion = (keys: readonly ViewOnlyProtectionKey[], preferredVersion: number) => {
  const preferred = keys.filter((k) => k.version === preferredVersion);
  const rest = keys.filter((k) => k.version !== preferredVersion);
  return [...preferred, ...rest];
};

export const decryptProtectedUrl = async (
  encryptedParameter: string,
  keys: readonly ViewOnlyProtectionKey[]
): Promise<string | undefined> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    return undefined;
  }

  const parsed = parseEnvelope(encryptedParameter);
  if (!parsed) {
    return undefined;
  }

  for (const { key } of orderKeysByPreferredVersion(keys, parsed.version)) {
    let keyBytes: Uint8Array<ArrayBuffer>;
    try {
      keyBytes = base64UrlToByteArray(key);
    } catch {
      continue;
    }

    try {
      const cryptoKey = await subtle.importKey("raw", keyBytes, AES_GCM, false, ["decrypt"]);
      const decrypted = await subtle.decrypt(
        { name: AES_GCM, iv: parsed.nonce, tagLength: TAG_SIZE_BYTES * 8 },
        cryptoKey,
        parsed.ciphertextWithTag
      );
      return new TextDecoder().decode(decrypted);
    } catch {
      // Wrong key or tampered payload — try the next candidate.
    }
  }

  return undefined;
};
